<template>
  <div class="rows mobile-cols">
    <div class="half">
      <DataGrid
          :actions-header="$t('settings.on-board.devices')"
          :data-model="dataGridModel"
          :data="filteredDevices"
          :selected-id="selectedDeviceId"
          @on-select="dataGridSelectDeviceId"
          @on-delete="deleteDeviceId"
          allow-add
          allow-edit
          allow-delete
      />
    </div>
    <div class="half">
      <div class="data-grid-form cols">
        <div class="rows phone-cols">
          <div class="half">
            <label for="deviceName">
              {{ $t('settings.on-board.devices.form.device-name') }}
            </label>
            <input
                id="deviceName"
                class="stretch"
                v-model="deviceName"
                @blur="v$.deviceName.$touch"
            />
            <p
                v-for="error of v$.deviceName.$errors"
                :key="error.$uid"
                :style="styleError"
            >
              <small> Error on property </small>
              <strong>{{ error.$property }}</strong>
              <small> is: </small>
              <strong>{{ error.$message }}</strong>
            </p>
          </div>
          <div class="half">
            <label for="deviceUniqueId">
              {{ $t('settings.on-board.devices.form.device-unique-id') }}
            </label>
            <input
                id="deviceUniqueId"
                class="stretch"
                v-model="deviceUniqueId"
                @blur="v$.deviceUniqueId.$touch"
            />
            <p
                v-for="error of v$.deviceUniqueId.$errors"
                :key="error.$uid"
                :style="styleError"
            >
              <small> Error on property </small>
              <strong>{{ error.$property }}</strong>
              <small> is: </small>
              <strong>{{ error.$message }}</strong>
            </p>
          </div>
        </div>
        <div class="rows phone-cols">
          <div class="half">
            <label for="deviceModel">
              {{ $t('settings.on-board.devices.form.device-model') }}
            </label>
            <select
                id="deviceModel"
                class="stretch"
                v-model="deviceModel"
                @blur="v$.deviceModel.$touch"
            >
              <option
                  v-for="(deviceModelText, deviceModelValue) in deviceModels"
                  v-bind:key="deviceModelValue"
                  :value="deviceModelValue"
              >
                {{ deviceModelText }}
              </option>
            </select>
            <p
                v-for="error of v$.deviceModel.$errors"
                :key="error.$uid"
                :style="styleError"
            >
              <small> Error on property </small>
              <strong>{{ error.$property }}</strong>
              <small> is: </small>
              <strong>{{ error.$message }}</strong>
            </p>
          </div>
        </div>
        <div class="rows phone-cols">
          <div class="half">
            <label for="vehicleMake">
              {{ $t('settings.on-board.devices.form.make') }}
            </label>
            <select
                id="vehicleMake"
                :class="['stretch', {'is-invalid': showStateByField('make') === false}]"
                v-model="make"
                @blur="v$.make.$touch"
            >
              <option :value="null">{{ $t('settings.generic.select-placeholder') }}</option>
              <option v-for="make in makesAlphabetical" v-bind:key="make.id" :value="make.id">
                {{ make.name }}
              </option>
            </select>
            <p
                v-for="error of v$.make.$errors"
                :key="error.$uid"
                :style="styleError"
            >
              <small> Error on property </small>
              <strong>{{ error.$property }}</strong>
              <small> is: </small>
              <strong>{{ error.$message }}</strong>
            </p>
          </div>
          <div class="half">
            <label for="vehicleModel">
              {{ $t('settings.on-board.devices.form.model') }}
            </label>
            <select
                id="vehicleModel"
                :class="['stretch', {'is-invalid': showStateByField('model') === false}]"
                v-model="model"
                @blur="v$.model.$touch"
            >
              <option :value="null">{{ $t('settings.generic.select-placeholder') }}</option>
              <option v-for="model in filteredModels" v-bind:key="model.id" :value="model.id">
                {{ model.name }}
              </option>
            </select>
            <p
                v-for="error of v$.model.$errors"
                :key="error.$uid"
                :style="styleError"
            >
              <small> Error on property </small>
              <strong>{{ error.$property }}</strong>
              <small> is: </small>
              <strong>{{ error.$message }}</strong>
            </p>
          </div>
        </div>
        <div class="rows phone-cols">
          <div class="half">
            <label for="vehicleLicensePlate">
              {{ $t('settings.on-board.devices.form.license-plate') }}
            </label>
            <input
                id="vehicleLicensePlate"
                class="stretch"
                :class="{'is-invalid': showStateByField('licensePlate') === false}"
                v-model="licensePlate"
                @blur="v$.licensePlate.$touch"
            />
            <p
                v-for="error of v$.licensePlate.$errors"
                :key="error.$uid"
                :style="styleError"
            >
              <small> Error on property </small>
              <strong>{{ error.$property }}</strong>
              <small> is: </small>
              <strong>{{ error.$message }}</strong>
            </p>
          </div>
          <div class="half">
            <label for="vehicleNickname">
              {{ $t('settings.car-fleet.form.vehicle-nickname') }}
            </label>
            <input
                id="vehicleNickname"
                class="stretch"
                :class="{'is-invalid': showStateByField('nickname') === false}"
                v-model="nickname"
                @blur="v$.nickname.$touch"
            />
            <p
                v-for="error of v$.nickname.$errors"
                :key="error.$uid"
                :style="styleError"
            >
              <small> Error on property </small>
              <strong>{{ error.$property }}</strong>
              <small> is: </small>
              <strong>{{ error.$message }}</strong>
            </p>
          </div>
        </div>
        <div class="rows phone-cols">
          <div class="half">
            <label for="vehicleLIDue">
              {{ $t('settings.car-fleet.form.liability-insurance-date') }}
            </label>
            <Datepicker
                id="vehicleLIDue"
                :model-value="liabilityInsuranceDue"
                @update:modelValue="selectLiabilityInsuranceDue"
                :format="$t('settings.generic.date-format')"
                :locale="locale"
                :enable-time-picker="false"
                :min-date="new Date()"
                auto-apply
                :state="showStateByField('liabilityInsuranceDue')"
            />
          </div>
          <div class="half">
            <label for="vehicleTIDue">
              {{ $t('settings.car-fleet.form.tech-inspection-date') }}
            </label>
            <Datepicker
                id="vehicleTIDue"
                :model-value="techInspectionDue"
                @update:modelValue="selectTechInspectionDue"
                :format="$t('settings.generic.date-format')"
                :locale="locale"
                :enable-time-picker="false"
                :min-date="new Date()"
                auto-apply
                :state="showStateByField('technicalInspectionDue')"
            />
          </div>
        </div>
        <div class="rows phone-cols">
          <div class="half">
            <label for="vehicleVignetteDue">
              {{ $t('settings.car-fleet.form.vignette-date') }}
            </label>
            <Datepicker
                id="vehicleVignetteDue"
                :model-value="vignetteDue"
                @update:modelValue="selectVignetteDue"
                :format="$t('settings.generic.date-format')"
                :locale="locale"
                :enable-time-picker="false"
                :min-date="new Date()"
                auto-apply
                :state="showStateByField('vignetteDue')"
            />
          </div>
          <div class="half">
            <label for="vehicleSeats">
              {{ $t('settings.on-board.devices.form.seats') }}
            </label>
            <input
                id="vehicleSeats"
                type="number"
                class="stretch"
                :class="{'is-invalid': showStateByField('seats') === false}"
                v-model="seats"
            />
          </div>
        </div>
        <div class="rows phone-cols">
          <div class="half">
            <label for="vehicleFuelExpenseUrban">
              {{ $t('settings.on-board.devices.form.fuel-expense-urban') }}
            </label>
            <input
                id="vehicleFuelExpenseUrban"
                type="number"
                class="stretch"
                :class="{'is-invalid': showStateByField('fuelExpenseUrban') === false}"
                v-model="fuelExpenseUrban"
                @blur="v$.fuelExpenseUrban.$touch"
            />
            <p
                v-for="error of v$.fuelExpenseUrban.$errors"
                :key="error.$uid"
                :style="styleError"
            >
              <small> Error on property </small>
              <strong>{{ error.$property }}</strong>
              <small> is: </small>
              <strong>{{ error.$message }}</strong>
            </p>
          </div>
          <div class="half">
            <label for="vehicleFuelExpenseNonUrban">
              {{ $t('settings.on-board.devices.form.fuel-expense-non-urban') }}
            </label>
            <input
                id="vehicleFuelExpenseNonUrban"
                type="number"
                class="stretch"
                :class="{'is-invalid': showStateByField('fuelExpenseNonUrban') === false}"
                v-model="fuelExpenseNonUrban"
                @blur="v$.fuelExpenseNonUrban.$touch"
            />
            <p
                v-for="error of v$.fuelExpenseNonUrban.$errors"
                :key="error.$uid"
                :style="styleError"
            >
              <small> Error on property </small>
              <strong>{{ error.$property }}</strong>
              <small> is: </small>
              <strong>{{ error.$message }}</strong>
            </p>
          </div>
        </div>
        <div class="rows phone-cols">
          <div class="half">
            <label for="vehicleDriverName">
              {{ $t('settings.on-board.devices.form.driver-name') }}
            </label>
            <input
                id="vehicleDriverName"
                class="stretch"
                :class="{'is-invalid': showStateByField('driverName') === false}"
                v-model="driverName"
                @blur="v$.driverName.$touch"
            />
            <p
                v-for="error of v$.driverName.$errors"
                :key="error.$uid"
                :style="styleError"
            >
              <small> Error on property </small>
              <strong>{{ error.$property }}</strong>
              <small> is: </small>
              <strong>{{ error.$message }}</strong>
            </p>
          </div>
          <div class="half">
            <label for="vehicleTankCapacity">
              {{ $t('settings.on-board.devices.form.tank-capacity') }}
            </label>
            <input
                id="vehicleTankCapacity"
                type="number"
                class="stretch"
                :class="{'is-invalid': showStateByField('tankCapacity') === false}"
                v-model="tankCapacity"
                @blur="v$.tankCapacity.$touch"
            />
            <p
                v-for="error of v$.tankCapacity.$errors"
                :key="error.$uid"
                :style="styleError"
            >
              <small> Error on property </small>
              <strong>{{ error.$property }}</strong>
              <small> is: </small>
              <strong>{{ error.$message }}</strong>
            </p>
          </div>
        </div>
        <div class="rows phone-cols">
          <div class="half cols">
            <div class="rows">
              <input
                  id="vehicleFuelCalculated"
                  type="checkbox"
                  v-model="fuelCalculated"
              />
              <label for="vehicleFuelCalculated">
                {{ $t('settings.on-board.devices.form.fuel-calculated') }}
              </label>
            </div>
            <div class="rows">
              <input
                  id="vehicleOohSupported"
                  type="checkbox"
                  v-model="oohSupported"
              />
              <label for="vehicleOohSupported">
                {{ $t('settings.on-board.devices.form.ooh-supported') }}
              </label>
            </div>
          </div>
          <div class="half pt-10">
            <label for="vehicleTripMinimumDistance">
              {{ $t('settings.on-board.devices.form.trip-minimum-distance') }}
            </label>
            <input
                id="vehicleTripMinimumDistance"
                type="number"
                min="-1"
                step="any"
                class="stretch"
                :class="{'is-invalid': showStateByField('tripMinimumDistance') === false ||
                    v$.tripMinimumDistance.$error}"
                v-model.number="tripMinimumDistance"
                @blur="v$.tripMinimumDistance.$touch"
            />
            <p
                v-for="error of v$.tripMinimumDistance.$errors"
                :key="error.$uid"
                :style="styleError"
            >
              <strong>{{ error.$message }}</strong>
            </p>
          </div>
        </div>
        <div class="rows phone-cols">
          <div class="half">
            <label for="vehicleTripConstructionMethod">
              {{ $t('settings.on-board.devices.form.trip-construction-method') }}
            </label>
            <select id="vehicleTripConstructionMethod" class="stretch" v-model="tripConstructionMethod">
              <option :value="TripConstructionMethod.Motion">
                {{ $t('settings.on-board.devices.form.trip-construction-method.motion') }}
              </option>
              <option :value="TripConstructionMethod.Stops">
                {{ $t('settings.on-board.devices.form.trip-construction-method.stops') }}
              </option>
            </select>
          </div>
          <div class="half">
            <label for="vehicleTripStopThresholdSeconds">
              {{ $t('settings.on-board.devices.form.trip-stop-threshold-seconds') }}
            </label>
            <input
                id="vehicleTripStopThresholdSeconds"
                type="number"
                min="0"
                step="1"
                class="stretch"
                v-model.number="tripStopThresholdSeconds"
                :disabled="tripConstructionMethod !== TripConstructionMethod.Stops"
            />
          </div>
        </div>

        <div>
          <button type="submit" class="button-save" @click="save">
            {{ $t('generic.save') }}
          </button>
        </div>

        <h3>{{ $t('settings.on-board.tasks') }}</h3>
        <TaskList :tasks="tasks"/>
      </div>
    </div>
  </div>
</template>

<script>
import DataGrid from '@/components/pages/partials/DataGrid';
import TaskList from '@/components/pages/partials/TaskList';
import {mapGetters, mapState} from 'vuex';
import {dataAggregator, dataManipulator, entityDataManager} from '@/assets/js/data-manipulation';
import Datepicker from '@vuepic/vue-datepicker';
import locale from '@/assets/js/locale';
import notification from '@/assets/js/notification';
import {actions} from '@/assets/js/vue/vuex/actions';
import {TripConstructionMethod} from '@/assets/js/enums/TripConstructionMethod.ts';
import { useVuelidate } from '@vuelidate/core'
import { required,integer,minLength,maxLength, helpers } from '@vuelidate/validators'

const licensePlateCheck = helpers.regex(/^[a-zA-Z0-9]*$/);
const positiveNumberOrEmpty = helpers.withMessage(
    'This field should contain a positive number',
    value => value == null || value === '' || (Number.isFinite(Number(value)) && Number(value) >= 0),
);

export default {

  setup: () => ({ v$: useVuelidate() }),
  validations () {
    return {
      deviceName: {required, $lazy: true},
      deviceUniqueId: {required,
        integer,
        minLenghtValue: minLength(14),
        maxLengthValue: maxLength(16),
        $lazy:true
      },
      deviceModel:{required, $lazy:true},
      make:{required, lazy:true},
      model:{required, lazy:true},
      licensePlate:{
        required: helpers.withMessage('This field cannot be empty', required),
        licensePlateCheck: helpers.withMessage('This field should contains only latin letters and no spaces', licensePlateCheck)
      },
      nickname:{required, lazy:true},
      fuelExpenseUrban:{required, lazy:true},
      fuelExpenseNonUrban:{required, lazy:true},
      driverName:{required, lazy:true},
      tankCapacity:{required, lazy:true},
      tripMinimumDistance:{positiveNumberOrEmpty, $lazy:true}
    }
  },

  name: 'Devices',
  components: {Datepicker, DataGrid, TaskList},
  props: {
    searchToken: String,
    selectedGroupId: [Number, null],
    selectedDeviceId: [Number, null],
  },
  data() {
    return {
      locale: locale.getLocale(),
      fieldStates: {},

      selectedVehicleId: null,

      deviceName: null,
      deviceUniqueId: null,
      deviceCategory: 'car',
      deviceModel: null,
      make: null,
      model: null,
      licensePlate: null,
      nickname: null,
      liabilityInsuranceDue: null,
      techInspectionDue: null,
      vignetteDue: null,
      seats: null,
      fuelExpenseUrban: null,
      fuelExpenseNonUrban: null,
      driverName: null,
      tankCapacity: null,
      fuelCalculated: false,
      tripMinimumDistance: null,
      TripConstructionMethod,
      tripConstructionMethod: TripConstructionMethod.Motion,
      tripStopThresholdSeconds: null,
      oohSupported: true,

      tasks: {
        taskCreateDevice: {
          text: this.$t('settings.on-board.devices.tasks.create-device'),
          status: 'pending',
        },
        taskCreateVehicle: {
          text: this.$t('settings.on-board.devices.tasks.create-vehicle'),
          status: 'pending',
        },
      },
      deviceModels: {
        'FMB003': 'FMB003',
        'FMB920': 'FMB920',
        'FMB140': 'FMB140',
        'FMP100': 'FMP100',
        'FMC225': 'FMC225',
        'TMT250': 'TMT250',
        'DashCam': 'DashCam',
      },
      styleError: {
        color: 'red',
        fontSize: '13px'
      }
    };
  },
  computed: {
    ...mapState(['devices', 'vehicles', 'lastErrorDetails']),
    ...mapGetters(['makesAlphabetical', 'modelsAlphabetical']),
    dataGridModel() {
      return {
        headers: {
          [this.$t('dashboard.devices.table.name')]: 'name',
          [this.$t('dashboard.devices.table.uid')]: 'uniqueId',
        },
      };
    },
    filteredDevices() {
      const groupDevices = entityDataManager.getDevicesByGroupId(this.devices, this.selectedGroupId);
      return dataAggregator.filterDevicesByToken(groupDevices, this.searchToken);
    },
    filteredModels() {
      return this.make === null ?
          this.modelsAlphabetical :
          this.modelsAlphabetical.filter(model => model.make === this.make);
    },
    hasErrors() {
      return Object.values(this.fieldStates).length > 0;
    },
  },
  watch: {
    selectedDeviceId(id) {
      this.fillFormByDeviceId(id);
    },
  },
  methods: {
    dataGridSelectDeviceId(id) {
      this.selectDeviceId(id);
      this.resetTasks();
    },
    selectDeviceId(id) {
      this.fillFormByDeviceId(id);
      this.$emit('on-select-device', id);
    },
    selectLiabilityInsuranceDue(value) {
      this.liabilityInsuranceDue = dataManipulator.dateToString(value);
    },
    selectTechInspectionDue(value) {
      this.techInspectionDue = dataManipulator.dateToString(value);
    },
    selectVignetteDue(value) {
      this.vignetteDue = dataManipulator.dateToString(value);
    },
    showDirtyFields() {
      this.fieldStates = {};

      if (this.lastErrorDetails === null) {
        return;
      }

      this.lastErrorDetails.forEach(error => {
        if (error.field !== undefined) {
          this.fieldStates[error.field] = false;
        }
      });
    },
    showStateByField(field) {
      return entityDataManager.getStateByField(this.fieldStates, field);
    },
    resetTasks() {
      for (let task in this.tasks) {
        this.tasks[task].status = 'pending';
      }
    },
    succeedTask(task) {
      this.tasks[task].status = 'success';
    },
    failTask(task) {
      this.tasks[task].status = 'fail';
    },
    fillFormByDeviceId(deviceId) {
      if (deviceId === null) {
        this.clearVehicleFields();

        this.deviceName = null;
        this.deviceUniqueId = null;
        this.deviceModel = null;

        return;
      }

      const device = entityDataManager.getById(this.devices, deviceId);

      if (device) {
        this.deviceName = device.name;
        this.deviceUniqueId = device.uniqueId;
        this.deviceModel = device.model;
      }

      const vehicle = entityDataManager.getVehicleByDeviceId(this.vehicles, deviceId);

      if (vehicle === null) {
        this.clearVehicleFields();

        return;
      }

      const model = entityDataManager.getModelByVehicle(this.modelsAlphabetical, vehicle);
      const make = entityDataManager.getMakeByModel(this.makesAlphabetical, model);

      this.selectedVehicleId = vehicle.id;
      this.licensePlate = vehicle.licensePlate;
      this.nickname = vehicle.nickname;
      this.seats = vehicle.seats;
      this.fuelExpenseUrban = vehicle.fuelExpenseUrban;
      this.fuelExpenseNonUrban = vehicle.fuelExpenseNonUrban;
      this.driverName = vehicle.driverName;
      this.tankCapacity = vehicle.tankCapacity;
      this.fuelCalculated = vehicle.fuelCalculated;
      this.tripMinimumDistance = vehicle.tripMinimumDistance ?? null;
      this.tripConstructionMethod = vehicle.tripConstructionMethod ?? TripConstructionMethod.Motion;
      this.tripStopThresholdSeconds = vehicle.tripStopThresholdSeconds ?? null;
      this.oohSupported = vehicle.oohSupported;

      if (make) {
        this.make = make.id;
      }

      if (model) {
        this.model = model.id;
      }

      this.selectLiabilityInsuranceDue(
          vehicle.liabilityInsuranceDue !== null ? new Date(vehicle.liabilityInsuranceDue) : null,
      );
      this.selectTechInspectionDue(
          vehicle.techInspectionDue !== null ? new Date(vehicle.techInspectionDue) : null,
      );
      this.selectVignetteDue(
          vehicle.vignetteDue !== null ? new Date(vehicle.vignetteDue) : null,
      );
    },
    clearVehicleFields() {
      this.selectedVehicleId = null;
      this.make = null;
      this.model = null;
      this.licensePlate = null;
      this.nickname = null;
      this.seats = null;
      this.fuelExpenseUrban = null;
      this.fuelExpenseNonUrban = null;
      this.driverName = null;
      this.tankCapacity = null;
      this.fuelCalculated = false;
      this.tripMinimumDistance = null;
      this.tripConstructionMethod = TripConstructionMethod.Motion;
      this.tripStopThresholdSeconds = null;
      this.oohSupported = true;
      this.selectLiabilityInsuranceDue(null);
      this.selectTechInspectionDue(null);
      this.selectVignetteDue(null);
    },
    async save() {
      const result = await this.v$.$validate()
      if (!result) {
        notification.error(this.$t('error.generic'));
        return
      }
      if (this.selectedGroupId === null) {
        notification.error(this.$t('settings.on-board.devices.group-required'));
        return;
      }

      const devicePayload = {
        id: this.selectedDeviceId,
        name: this.deviceName,
        uniqueId: this.deviceUniqueId,
        category: this.deviceCategory,
        model: this.deviceModel,
        groupId: this.selectedGroupId,
      };

      const device = await this.saveDevice(devicePayload);

      if (device === null) {
        return;
      }

      const vehiclePayload = {
        id: this.selectedVehicleId,
        deviceId: device.id,
        model: this.model,
        licensePlate: this.licensePlate,
        nickname: this.nickname,
        seats: parseInt(this.seats) || 0,
        fuelExpenseUrban: parseInt(this.fuelExpenseUrban) || 0,
        fuelExpenseNonUrban: parseInt(this.fuelExpenseNonUrban) || 0,
        driverName: this.driverName,
        tankCapacity: this.tankCapacity,
        fuelCalculated: this.fuelCalculated,
        tripMinimumDistance: this.tripMinimumDistance === '' ? null : this.tripMinimumDistance,
        tripConstructionMethod: this.tripConstructionMethod,
        tripStopThresholdSeconds: this.tripStopThresholdSeconds === '' ? null : this.tripStopThresholdSeconds,
        oohSupported: this.oohSupported,
        liabilityInsuranceDue: this.liabilityInsuranceDue,
        techInspectionDue: this.techInspectionDue,
        vignetteDue: this.vignetteDue,
      };

      await this.saveVehicle(vehiclePayload);

      await this.$store.dispatch(actions.GET_VEHICLES).catch(e => notification.error(this.$t(e.message)));
      await this.$store.dispatch(actions.GET_DEVICES).catch(e => notification.error(this.$t(e.message)));

      if (!this.hasErrors) {
        this.selectDeviceId(device.id);
      }
    },
    async saveDevice(payload) {
      let resultDevice = null;

      const action = payload.id === null ? actions.CREATE_DEVICE : actions.EDIT_DEVICE;

      await this.$store.dispatch(action, payload).
          then(device => {
            resultDevice = device;
            this.succeedTask('taskCreateDevice');
          }).
          catch(() => this.failTask('taskCreateDevice'));

      return resultDevice;
    },
    async saveVehicle(payload) {
      let resultVehicle = null;

      const action = payload.id === null ? actions.CREATE_VEHICLE : actions.EDIT_VEHICLE;

      await this.$store.dispatch(action, payload).
          then(vehicle => {
            resultVehicle = vehicle;
            this.succeedTask('taskCreateVehicle');
          }).
          catch(() => this.failTask('taskCreateVehicle')).
          finally(() => this.showDirtyFields());

      return resultVehicle;
    },
    deleteDeviceId(id) {
      if (confirm(this.$t('generic.delete.confirm'))) {
        this.$store.dispatch(actions.DELETE_DEVICE, {id}).
            then(() => {
              const vehicleId = entityDataManager.getVehicleIdByDeviceId(this.vehicles, id);

              if (vehicleId !== null) {
                this.$store.dispatch(actions.DELETE_VEHICLE, {id: vehicleId}).
                    then(() => {
                      this.$store.dispatch(actions.GET_VEHICLES).catch(e => notification.error(this.$t(e.message)));
                    }).
                    catch(e => notification.error(this.$t(e.message)));
              }

              this.selectDeviceId(null);
              this.$store.dispatch(actions.GET_DEVICES).catch(e => notification.error(this.$t(e.message)));
              notification.success(this.$t('generic.delete.success'));
            }).
            catch(e => notification.error(this.$t(e.message)));
      }
    },
  },
};
</script>
